import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Shimmer } from '@wartek-id/shimmer'

import { Header } from 'app/KinerjaShared/components/Header'
import { TablePenetapan } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan'
import { StatusFilterPPK } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Filters'
import useBasePath from 'app/KinerjaTimPenilaiPSPage/hooks/useBasePath'
import {
  useAPIGetPenetapanPegawai,
  useAPIGetPenetapanPegawaiCountStatus,
  useAPIGetPenetapanPegawaiCountPredicate,
} from 'api/kinerja/tim-penilai-ps/penetapan'
import { PANDUAN_PENETAPAN_PREDIKAT_KINERJA_PPK_URL } from 'app/KinerjaPSPegawaiPage/constants/panduan'
import { usePenetapanTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePenetapanTrackEvent'

import type { PenetapanPegawaiFilterEnum } from 'types/kinerja-tim-penilai-ps'

const PAGE_SIZE = 10

export default function PenetapanPegawaiList() {
  const router = useRouter()
  const { basePath } = useBasePath()
  const { trackEvent } = usePenetapanTrackEvent()

  const [page, setPage] = useState(1)

  // Filters
  const [activeStatus, setActiveStatus] = useState<string>('ALL')

  // Get Penetapan Pegawai List
  const { data: responseData, isFetching } = useAPIGetPenetapanPegawai({
    params: {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      status:
        activeStatus === 'ALL'
          ? ''
          : (activeStatus as PenetapanPegawaiFilterEnum),
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      onSuccess: () => {
        trackEvent('tabel pegawai PPK', 'view')
      },
    },
  })
  const { data: statusCountData, isFetching: isStatusCountFetching } =
    useAPIGetPenetapanPegawaiCountStatus({
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })
  const { data: predicateCount, isFetching: isPredicateCountFetching } =
    useAPIGetPenetapanPegawaiCountPredicate({
      params: {
        status: 'SENT_TO_PPK',
      },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const data = useMemo(() => responseData?.data, [responseData?.data])
  const meta = useMemo(() => responseData?.meta, [responseData?.meta])
  const hasSentToPpk = useMemo(
    () => statusCountData?.statusCount?.sentToPpk,
    [statusCountData]
  )
  const showPagination = meta?.total > PAGE_SIZE

  return (
    <div className="min-h-full px-8 pt-12 pb-20 bg-gray-10">
      <div className="pb-12 mx-auto max-w-screen-2xl">
        <Header
          title="Penetapan Predikat Kinerja"
          className="mb-12"
          rightNode={
            <div className="bg-white rounded">
              <Button
                as="a"
                variant="ghost"
                rel="noopener noreferrer"
                className="flex gap-1.5 h-14"
                size="md"
                href={PANDUAN_PENETAPAN_PREDIKAT_KINERJA_PPK_URL}
                target="_blank"
              >
                <Icon fontSize="sm" color="informational">
                  help
                </Icon>
                Baca panduan
              </Button>
            </div>
          }
        />

        <main>
          <div className="p-6 space-y-6 bg-white rounded-t-lg">
            <div className="space-y-1">
              <Text variant="heading-md">
                Kirim Penetapan Predikat Kinerja ke Pengawas Sekolah
              </Text>
              <Text>
                Predikat Kinerja{' '}
                <strong>direkomendasikan oleh Tim Kinerja</strong> dan mungkin
                muncul secara berkala. Silakan ubah atau langsung kirim ke
                Pengawas Sekolah.
              </Text>
            </div>
            <div
              className={clsx(
                'grid grid-cols-12 px-4 py-3 border rounded-md ',
                hasSentToPpk &&
                  'bg-surface-selected-default border-interactive-default',
                !hasSentToPpk && 'bg-surface-subdued border-subdued'
              )}
            >
              {isStatusCountFetching || isPredicateCountFetching ? (
                <div className="grid grid-cols-12 col-span-12 gap-6">
                  <div className="col-span-6">
                    <Shimmer className="!w-6/12 mb-2" height={20} />
                    <Shimmer className="!w-8/12 mb-4" height={10} />
                    <Shimmer width={120} height={40} />
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-6 space-y-3">
                        <Shimmer className="!w-full" height={20} />
                        <Shimmer className="!w-full" height={20} />
                        <Shimmer className="!w-full" height={20} />
                      </div>
                      <div className="col-span-6 space-y-3">
                        <Shimmer className="!w-full" height={20} />
                        <Shimmer className="!w-full" height={20} />
                        <Shimmer className="!w-full" height={20} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="col-span-6 space-y-4">
                    <div className="space-y-1">
                      {hasSentToPpk ? (
                        <Text variant="heading-sm" color="informational">
                          {statusCountData?.statusCount?.sentToPpk ?? 0}{' '}
                          Penetapan Predikat Kinerja dapat dikirim
                        </Text>
                      ) : (
                        <Text variant="heading-sm" color="subdued">
                          Belum ada Predikat Kinerja yang dapat dikirim
                        </Text>
                      )}
                    </div>

                    <Button
                      className="!space-x-1"
                      disabled={!hasSentToPpk}
                      onClick={() => {
                        router.push(`${basePath}/penilai/penetapan/kirim`)
                      }}
                    >
                      <Icon color={hasSentToPpk ? 'inverse' : 'disabled'}>
                        mail_outline
                      </Icon>
                      <span>
                        Kirim ({statusCountData?.statusCount?.sentToPpk ?? 0})
                      </span>
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 col-span-6 gap-4">
                    {[
                      [
                        { label: 'Sangat Baik', key: 'veryGood' },
                        { label: 'Baik', key: 'good' },
                        { label: 'Butuh Perbaikan', key: 'needImprovement' },
                      ],
                      [
                        { label: 'Kurang', key: 'bad' },
                        { label: 'Sangat Kurang', key: 'veryBad' },
                      ],
                    ].map((group, groupIdx) => (
                      <div
                        key={groupIdx}
                        className={
                          groupIdx === 0
                            ? 'pr-6 space-y-3 border-r border-subdued'
                            : 'space-y-3'
                        }
                      >
                        {group.map(({ label, key }) => (
                          <div
                            className="flex items-center justify-between"
                            key={key}
                          >
                            <Text variant="action" color="subdued">
                              {label}
                            </Text>
                            <Text variant="body-sm">
                              {predicateCount?.predikatKinerjaCount?.[key]
                                ? `${predicateCount?.predikatKinerjaCount?.[key]} orang`
                                : 0}
                            </Text>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <StatusFilterPPK
              activeStatus={activeStatus}
              setActiveStatus={setActiveStatus}
              countData={statusCountData}
            />
          </div>
          <TablePenetapan.Provider
            data={data}
            meta={meta}
            isFetching={isFetching}
          >
            <div className="space-y-4">
              <TablePenetapan.Table
                nextPageResolver={(pengawasSekolahId) =>
                  `${basePath}/penilai/penetapan/${pengawasSekolahId}`
                }
              />
              {showPagination && (
                <TablePenetapan.Pagination
                  totalItems={meta?.total}
                  pageSize={PAGE_SIZE}
                  currentPage={page}
                  onPageChange={setPage}
                />
              )}
            </div>
          </TablePenetapan.Provider>
        </main>
      </div>
    </div>
  )
}
